import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEvaluation } from '@/hooks/useEvaluation';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { NonAttendanceType, type NonAttendance } from '@/types/overview';
import { formatDateWithWeekday, formatShortDateTime } from '@/utils/date';
import { joinNonEmpty } from '@/utils/text';
import { tv } from '@/utils/tv';

interface Props {
  nonAttendance: NonAttendance;
}

const valueBadge = tv({
  base: 'text-subhead-14 flex h-6 min-w-6 items-center justify-center rounded-xl px-2 text-foreground',
  variants: {
    isExcused: {
      true: 'bg-on-primary-surface',
      false: 'bg-red-soft',
    },
  },
});

export const AbsenceDetail: React.FC<Props> = ({ nonAttendance }) => {
  const { t } = useTranslation();
  const { data: detail, isLoading } = useEvaluation(nonAttendance.id);

  if (isLoading || !detail) {
    return (
      <div className="bg-background absolute inset-0 flex items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  const { evaluation, lesson } = detail;
  const { nonAttendanceType: type } = evaluation;
  const { Sick, Other } = NonAttendanceType;
  const isExcused = type === Sick || type === Other;
  const description =
    evaluation.testNotes || t('home.dashboard.absences.noDescription');
  const meta = joinNonEmpty([
    evaluation.authorName,
    formatShortDateTime(evaluation.timeCreated),
  ]);

  return (
    <div className="flex flex-col gap-5 px-4 pb-4 md:px-5 md:pb-5">
      <div className="bg-card shadow-card flex flex-col gap-3 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-body-13 md:text-body-14 truncate text-subtle-foreground">
            {lesson.type.name}
          </span>
          <span className="text-body-13 md:text-body-14 shrink-0 text-subtle-foreground">
            {formatDateWithWeekday(lesson.lessonDate)}
          </span>
        </div>
        <p className="text-body-13 md:text-body-14 text-foreground">
          {description}
        </p>
      </div>

      <section className="flex flex-col gap-3">
        <h3 className="text-callout-16 text-foreground">
          {t('home.dashboard.absences.marks')}
        </h3>
        <div className="bg-card shadow-card flex min-h-[78px] flex-col gap-3 rounded-[10px] px-4 py-3">
          <div className="flex items-center justify-end gap-4">
            <span className="text-body-13 md:text-body-14 min-w-0 flex-1 truncate text-muted-foreground">
              {t('home.dashboard.absences.absence')}
            </span>
            <div className="relative flex shrink-0 items-center">
              <span className={valueBadge({ isExcused })}>
                {evaluation.value}
              </span>
              {isExcused && (
                <span className="text-caption-10 bg-blue-soft text-foreground absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full font-semibold leading-none">
                  {evaluation.nonAttendanceType}
                </span>
              )}
            </div>
          </div>
          {meta && (
            <p className="text-caption-12 text-right text-subtle-foreground">
              {meta}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};
